import React, { useState, useEffect, useCallback } from 'react';
import { BrowserRouter as Router, Routes, Route, useLocation } from 'react-router-dom';
import './App.css';
import config from './config';
import Navigation from './components/Navigation';
import New from './pages/New';
import CalendarPage from './pages/CalendarPage';
import Upload from './pages/Upload';
import SharePage from './pages/SharePage';
import AccountSettingsPage from './pages/AccountSettingsPage';
import FamilySharePage from './pages/FamilySharePage';
import ClassSetupPage from './pages/ClassSetupPage';
import ClassInvitePage from './pages/ClassInvitePage';
import ShareDebug from './components/ShareDebug';
import UserMigration from './components/UserMigration';
import UpdateArbor1 from './pages/UpdateArbor1';
import EditEventModal from './components/EditEventModal';
import Login from './components/Login';
import SignUp from './components/SignUp';
import PrivateRoute from './components/PrivateRoute';
import { AuthProvider } from './contexts/AuthContext';
import { useAuth } from './contexts/AuthContext';
import { db } from './firebase';
import { collection, addDoc, getDocs, updateDoc, doc, deleteDoc, onSnapshot, query, where, orderBy, or, getDoc } from 'firebase/firestore';
import { getCalendarUsers } from './services/sharingService';
import { getUserFamilyInfo, getFamilyMembers, FAMILY_ROLES } from './services/familyService';
import { useFamilyRole } from './hooks/useFamilyRole';
import { useClassRole } from './hooks/useClassRole';
import { classEventService } from './services/classEventService';
import { trackSession } from './utils/analytics';

// Import the PageTransition component
import PageTransition from './components/PageTransition';

// Helper function to get user name from Firebase users collection
const getUserName = async (userId, fallbackEmail) => {
  try {
    const userDoc = await getDoc(doc(db, 'users', userId));
    if (userDoc.exists()) {
      const userData = userDoc.data();
      if (userData.firstName && userData.lastName) {
        return `${userData.firstName} ${userData.lastName}`;
      } else if (userData.firstName) {
        return userData.firstName;
      } else if (userData.lastName) {
        return userData.lastName;
      }
    }
    // Fallback to email if no name found
    return fallbackEmail ? fallbackEmail.split('@')[0] : 'User';
  } catch (error) {
    console.error('Error fetching user name:', error);
    return fallbackEmail ? fallbackEmail.split('@')[0] : 'User';
  }
};

// Component to conditionally render mobile navigation based on current route
function MobileNavigation({ isMobile }) {
  const location = useLocation();
  
  // Show navigation on all pages including calendar for mobile
  return (
    <div className="bottom-navigation">
      <Navigation />
    </div>
  );
}

// Component to conditionally apply calendar page class to app container
function AppContainerWrapper({ children }) {
  const location = useLocation();
  const isCalendarPage = location.pathname === '/calendar';
  
  return (
    <div className={`app-container${isCalendarPage ? ' calendar-page' : ''}`}>
      {children}
    </div>
  );
}

// Check if two events are duplicates
const checkForDuplicate = (newEvent, existingEvents) => {
  // If no events to compare against, no duplicates
  if (!existingEvents || existingEvents.length === 0) {
    return false;
  }

  // Check for exact same day, month, year
  const possibleDuplicates = existingEvents.filter(event => 
    event.day === newEvent.day && 
    event.month === newEvent.month && 
    event.year === newEvent.year
  );

  if (possibleDuplicates.length === 0) {
    return false;
  }

  // For events on the same day, check for time overlap
  for (const existingEvent of possibleDuplicates) {
    // First, check if both events have specific times
    if (newEvent.hasTime && existingEvent.hasTime) {
      // Convert times to minutes since start of day for easier comparison
      const newEventStart = (newEvent.hour * 60) + (newEvent.minute || 0);
      const existingEventStart = (existingEvent.hour * 60) + (existingEvent.minute || 0);
      
      // Calculate end times
      const newEventDuration = newEvent.eventDuration || 60; // Default 1 hour
      const existingEventDuration = existingEvent.eventDuration || 60; // Default 1 hour
      
      const newEventEnd = newEventStart + newEventDuration;
      const existingEventEnd = existingEventStart + existingEventDuration;
      
      // Check for time overlap
      const timeOverlap = (
        // New event starts during existing event
        (newEventStart >= existingEventStart && newEventStart < existingEventEnd) ||
        // Existing event starts during new event
        (existingEventStart >= newEventStart && existingEventStart < newEventEnd)
      );
      
      if (!timeOverlap) {
        continue; // Skip to next event if no time overlap
      }
    }
    
    // At this point, either the events are on the same day with overlapping times,
    // or at least one of them is an all-day event.
    
    // Check if event names are similar
    const nameSimilarity = compareName(newEvent.eventName, existingEvent.eventName);
    if (nameSimilarity >= 0.7) { // 70% similarity threshold
      // Now check other properties for more confidence
      const hasSameCategory = newEvent.category === existingEvent.category;
      const hasSameLocation = newEvent.location === existingEvent.location;
      const hasSameParticipants = compareParticipants(newEvent, existingEvent);
      
      // If name is very similar and at least one other property matches, consider it a duplicate
      if (nameSimilarity > 0.8 || (nameSimilarity >= 0.7 && (hasSameCategory || hasSameLocation || hasSameParticipants))) {
        return true;
      }
    }
  }
  
  return false;
};

// Compare event names for similarity
const compareName = (name1, name2) => {
  if (!name1 || !name2) return 0;
  
  // Convert both names to lowercase for comparison
  const n1 = name1.toLowerCase();
  const n2 = name2.toLowerCase();
  
  // Exact match
  if (n1 === n2) return 1.0;
  
  // Check if one contains the other
  if (n1.includes(n2) || n2.includes(n1)) return 0.9;
  
  // Simple word comparison - count shared words
  const words1 = n1.split(/\s+/).filter(word => word.length > 2); // Skip very short words
  const words2 = n2.split(/\s+/).filter(word => word.length > 2);
  
  // If either has no significant words, return low similarity
  if (words1.length === 0 || words2.length === 0) return 0.3;
  
  let matchedWords = 0;
  for (const word1 of words1) {
    for (const word2 of words2) {
      // Check for exact word match or if one word contains the other
      if (word1 === word2 || 
          (word1.length > 4 && word2.length > 4 && 
           (word1.includes(word2) || word2.includes(word1)))) {
        matchedWords++;
        break;
      }
    }
  }
  
  // Calculate similarity score
  const totalWords = Math.max(words1.length, words2.length);
  return matchedWords / totalWords;
};

// Compare participants between events
const compareParticipants = (event1, event2) => {
  // Handle different participant formats
  const getParticipants = (event) => {
    if (event.participants && Array.isArray(event.participants)) {
      return event.participants;
    } else if (event.participant) {
      return event.participant.split(',').map(p => p.trim());
    }
    return [];
  };
  
  const participants1 = getParticipants(event1);
  const participants2 = getParticipants(event2);
  
  // If either has no participants, they can't match
  if (participants1.length === 0 || participants2.length === 0) return false;
  
  // Check for at least one common participant
  return participants1.some(p1 => 
    participants2.some(p2 => 
      p1.toLowerCase() === p2.toLowerCase() || 
      p1.toLowerCase().includes(p2.toLowerCase()) || 
      p2.toLowerCase().includes(p1.toLowerCase())
    )
  );
};

// Mobile container component for desktop users
const MobileViewportContainer = ({ children }) => {
  // Always return children directly - no container wrapper
  return children;
};

function AppContent() {
  const { currentUser } = useAuth();
  const { isClassAdmin, classInfo } = useClassRole();
  const [inputText, setInputText] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [eventToEdit, setEventToEdit] = useState(null);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);

  // Track session end on app unmount or page close
  useEffect(() => {
    const handleBeforeUnload = () => {
      trackSession.end();
    };
    
    // Track page visibility for better engagement measurement
    const handleVisibilityChange = () => {
      if (document.hidden) {
        // Page is now hidden
        trackSession.stopEngagementTracking();
      } else {
        // Page is now visible
        trackSession.startEngagementTracking();
      }
    };
    
    window.addEventListener('beforeunload', handleBeforeUnload);
    document.addEventListener('visibilitychange', handleVisibilityChange);
    
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      trackSession.end();
    };
  }, []);

  // Initialize state with empty arrays, will fetch from Firebase
  const [pendingEvents, setPendingEvents] = useState([]);
  const [acceptedEvents, setAcceptedEvents] = useState([]);
  const [sharedUserIds, setSharedUserIds] = useState([]);
  const [userFamilyRole, setUserFamilyRole] = useState(null);
  const [familyRoleLoaded, setFamilyRoleLoaded] = useState(false);

  // Use family role hook for permission-based filtering
  const { 
    needsEventFiltering, 
    canViewAllEvents, 
    loading: familyRoleLoading 
  } = useFamilyRole();

  // Use API key from config file
  const API_KEY = config.OPENAI_API_KEY;

  // Reference to Firestore collections
  const pendingEventsRef = collection(db, "pendingEvents");
  const acceptedEventsRef = collection(db, "acceptedEvents");

  // Function to refresh shared users
  const refreshSharedUsers = useCallback(async () => {
    if (!currentUser) return;
    
    try {
      // Get calendar share users
      const users = await getCalendarUsers(currentUser.uid);
      const calendarUserIds = users
        .filter(u => u.userId !== currentUser.uid)
        .map(u => u.userId)
        .filter(id => id !== null);
      
      // Get family members
      const familyInfo = await getUserFamilyInfo(currentUser.uid);
      let familyUserIds = [];
      
      if (familyInfo?.familyId) {
        const familyMembers = await getFamilyMembers(familyInfo.familyId);
        familyUserIds = familyMembers
          .filter(member => member.userId !== currentUser.uid)
          .map(member => member.userId);
        console.log('👨‍👩‍👧‍👦 Family members found:', familyUserIds);
      }
      
      // Combine calendar shares and family members
      const allSharedUserIds = [...new Set([...calendarUserIds, ...familyUserIds])];
      setSharedUserIds(allSharedUserIds);
      console.log('Refreshed shared users (calendar + family):', allSharedUserIds);
    } catch (error) {
      console.error('Error fetching shared users:', error);
    }
  }, [currentUser]);

  // Fetch shared users when component mounts
  useEffect(() => {
    refreshSharedUsers();
  }, [currentUser]);

  // Handle OAuth callback from backend authentication
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    
    if (urlParams.get('auth') === 'success') {
      const accessToken = urlParams.get('token');
      const expiresIn = parseInt(urlParams.get('expiresIn'));
      
      if (accessToken && expiresIn) {
        console.log('🔐 Backend OAuth callback received');
        console.log(`  - Token expires in: ${expiresIn / 1000 / 60} minutes`);
        
        // Store tokens for both Google Calendar and Gmail
        const expiryTime = Date.now() + expiresIn;
        localStorage.setItem('googleAccessToken', accessToken);
        localStorage.setItem('googleTokenExpiry', expiryTime.toString());
        localStorage.setItem('googleTokenExpiresIn', (expiresIn / 1000).toString());
        localStorage.setItem('googleAutoRefresh', 'true');
        
        // Also store for email service
        localStorage.setItem('emailAccessToken', accessToken);
        localStorage.setItem('emailTokenExpiry', expiryTime.toString());
        localStorage.setItem('emailTokenExpiresIn', (expiresIn / 1000).toString());
        localStorage.setItem('emailAutoRefresh', 'true');
        
        // Mark backend auth as enabled
        localStorage.setItem('backendAuthEnabled', 'true');
        
        // Clean URL
        window.history.replaceState({}, document.title, window.location.pathname);
        
        // Show success message
        console.log('✅ Backend authentication successful! Tokens stored.');
        
        // Reload to initialize with new auth
        setTimeout(() => {
          window.location.reload();
        }, 1000);
      }
    } else if (urlParams.get('auth') === 'error') {
      console.error('❌ Backend OAuth failed:', urlParams.get('error'));
      // Clean URL
      window.history.replaceState({}, document.title, window.location.pathname);
    }
  }, []);

  // Fetch events from Firebase when component mounts
  useEffect(() => {
    if (!currentUser) {
      setPendingEvents([]);
      setAcceptedEvents([]);
      setUserFamilyRole(null);
      return;
    }

    // Get user's family role
    const fetchFamilyRole = async () => {
      try {
        const familyInfo = await getUserFamilyInfo(currentUser.uid);
        console.log('🎭 Family role fetched:', familyInfo?.role);
        setUserFamilyRole(familyInfo?.role || null);
      } catch (error) {
        console.error('Error fetching family role:', error);
        setUserFamilyRole(null);
      } finally {
        setFamilyRoleLoaded(true);
      }
    };
    
    fetchFamilyRole();

    // Build the query conditions for shared users
    const userConditions = [
      where("userId", "==", currentUser.uid),
      where("userId", "==", null)
    ];
    
    // Add conditions for each shared user
    sharedUserIds.forEach(userId => {
      userConditions.push(where("userId", "==", userId));
    });

    // Set up real-time listener for pending events filtered by userId or shared users
    const pendingEventsQuery = query(
      pendingEventsRef, 
      or(...userConditions),
      orderBy("createdAt", "desc")
    );
    const unsubscribePending = onSnapshot(pendingEventsQuery, async (snapshot) => {
      const allEvents = [];
      const sharedEventsToUpdate = [];
      
      snapshot.forEach(doc => {
        const eventData = doc.data();
        console.log('📘 Raw event from Firebase:', {
          docId: doc.id,
          eventId: eventData.id,
          source: eventData.source,
          eventName: eventData.eventName,
          isSharedEvent: eventData.isSharedEvent,
          sharedWithEmail: eventData.sharedWithEmail
        });
        
        // Check if this is a shared event for the current user
        if (eventData.isSharedEvent && 
            eventData.sharedWithEmail === currentUser.email && 
            eventData.userId === null) {
          sharedEventsToUpdate.push({
            docId: doc.id,
            eventData: eventData
          });
        }
        
        // Store the Firestore document ID and ensure it's used as the primary ID
        const processedEvent = {
          ...eventData,
          id: doc.id, // ALWAYS use Firestore document ID as the primary ID
          originalId: eventData.id, // Keep the original ID for reference
          firestoreId: doc.id // Store Firestore document ID separately
        };
        allEvents.push(processedEvent);
      });
      
      // Filter out duplicate events from shared calendars
      const uniqueEvents = [];
      const seenEvents = new Map();
      
      for (const event of allEvents) {
        // Create a unique key based on event details
        const eventKey = `${event.eventName?.toLowerCase()}-${event.day}-${event.month}-${event.year}-${event.hour || 'notime'}-${event.minute || 0}`;
        
        if (!seenEvents.has(eventKey)) {
          seenEvents.set(eventKey, event);
          uniqueEvents.push(event);
        } else {
          // If duplicate found, prefer the event owned by current user
          const existingEvent = seenEvents.get(eventKey);
          if (event.userId === currentUser.uid && existingEvent.userId !== currentUser.uid) {
            // Replace with current user's event
            const index = uniqueEvents.findIndex(e => e.id === existingEvent.id);
            if (index !== -1) {
              uniqueEvents[index] = event;
              seenEvents.set(eventKey, event);
            }
          }
          console.log(`🔍 Duplicate pending event filtered: "${event.eventName}" (owned by ${event.userId === currentUser.uid ? 'you' : 'shared user'})`);
        }
      }
      
      // Update shared events to assign them to the current user
      if (sharedEventsToUpdate.length > 0) {
        console.log(`Found ${sharedEventsToUpdate.length} shared events to claim for ${currentUser.email}`);
        
        try {
          const updatePromises = sharedEventsToUpdate.map(({ docId }) =>
            updateDoc(doc(db, "pendingEvents", docId), {
              userId: currentUser.uid,
              claimedAt: new Date().toISOString()
            })
          );
          
          await Promise.all(updatePromises);
          console.log('Successfully claimed shared events');
        } catch (error) {
          console.error('Error claiming shared events:', error);
        }
      }
      
      currentPersonalPendingEvents = uniqueEvents;
      (async () => {
        await processPendingEvents([...currentPersonalPendingEvents, ...currentClassPendingEvents]);
      })();
      console.log('📊 Fetched pending events from Firebase:', allEvents.length);
      console.log('📊 After duplicate filtering:', uniqueEvents.length);
    }, (error) => {
      console.error("Error fetching pending events:", error);
    });

    // First, get the user's classID
    const getUserClassID = async () => {
      try {
        const userQuery = query(collection(db, 'users'), where('uid', '==', currentUser.uid));
        const userSnapshot = await getDocs(userQuery);
        if (!userSnapshot.empty) {
          const userData = userSnapshot.docs[0].data();
          return userData.classID || null;
        }
        return null;
      } catch (error) {
        console.error('Error fetching user classID:', error);
        return null;
      }
    };

    // Set up real-time listener for accepted events filtered by userId or shared users
    const acceptedEventsQuery = query(
      acceptedEventsRef,
      or(...userConditions),
      orderBy("createdAt", "desc")
    );
    const unsubscribeAccepted = onSnapshot(acceptedEventsQuery, async (snapshot) => {
      currentPersonalEvents = [];
      snapshot.forEach(doc => {
        // Store the Firestore document ID in a special property for deletion
        currentPersonalEvents.push({
          id: doc.id, // Use Firestore document ID
          ...doc.data(),
          firestoreId: doc.id, // Store Firestore document ID separately
          isPersonalEvent: true // Mark as personal event
        });
      });
      
      // Process events after getting both personal and class events
      (async () => {
        await processAllEvents([...currentPersonalEvents, ...currentClassEvents]);
      })();
    }, (error) => {
      console.error("Error fetching accepted events:", error);
    });

    // Store personal and class events separately
    let currentPersonalEvents = [];
    let currentClassEvents = [];
    let currentPersonalPendingEvents = [];
    let currentClassPendingEvents = [];
    
    // Set up separate listeners for Arbor1 collections
    let unsubscribeArbor1 = null;
    let unsubscribeArbor1Pending = null;
    
    const setupArbor1Listeners = async () => {
      console.log('🏫 Setting up Arbor1 listeners...');
      const userClassID = await getUserClassID();
      console.log('🏫 User classID:', userClassID);
      
      if (userClassID === 'ArborSchool-1ST(Grade One)') {
        console.log('🏫 User has Arbor School class, setting up listeners...');
        try {
          // Listen to Arbor1 accepted events
          const arbor1Ref = collection(db, 'Arbor1');
          const arbor1Query = query(arbor1Ref, where('classID', '==', userClassID));
          
          unsubscribeArbor1 = onSnapshot(arbor1Query, (snapshot) => {
            currentClassEvents = [];
            
            snapshot.forEach(doc => {
              const eventData = doc.data();
              currentClassEvents.push({
                id: doc.id,
                ...eventData,
                firestoreId: doc.id,
                isClassEvent: true,
                collectionSource: 'Arbor1'
              });
            });
            
            console.log(`📚 Fetched ${currentClassEvents.length} accepted class events from Arbor1`);
            (async () => {
              await processAllEvents([...currentPersonalEvents, ...currentClassEvents]);
            })();
          }, (error) => {
            console.error('Error fetching Arbor1 events:', error);
          });
          
          // Listen to Arbor1PendingEvents
          const arbor1PendingRef = collection(db, 'Arbor1PendingEvents');
          const arbor1PendingQuery = query(arbor1PendingRef, where('classID', '==', userClassID));
          
          unsubscribeArbor1Pending = onSnapshot(arbor1PendingQuery, (snapshot) => {
            currentClassPendingEvents = [];
            
            snapshot.forEach(doc => {
              const eventData = doc.data();
              currentClassPendingEvents.push({
                id: doc.id,
                ...eventData,
                firestoreId: doc.id,
                isClassEvent: true,
                collectionSource: 'Arbor1PendingEvents'
              });
            });
            
            console.log(`📚 Fetched ${currentClassPendingEvents.length} pending class events from Arbor1PendingEvents`);
            (async () => {
              await processPendingEvents([...currentPersonalPendingEvents, ...currentClassPendingEvents]);
            })();
          }, (error) => {
            console.error('Error fetching Arbor1 pending events:', error);
          });
        } catch (error) {
          console.error('Error setting up Arbor1 listeners:', error);
        }
      } else {
        console.log('🏫 User does not have Arbor School class or classID not found');
      }
    };

    // Function to process and deduplicate all events
    const processAllEvents = async (allEvents) => {
      
      // Filter events based on permissions - only show events they're assigned to if they don't have view all permission
      let filteredEvents = allEvents;
      console.log('👤 User permissions - needsEventFiltering:', needsEventFiltering, 'canViewAllEvents:', canViewAllEvents, 'Family role loading:', familyRoleLoading);
      
      // IMPORTANT: Only filter if family role is loaded AND user needs event filtering (doesn't have view all permission)
      if (!familyRoleLoading && needsEventFiltering) {
        // Use user's email for matching since we're now using emails in participants
        const userEmail = currentUser.email;
        
        filteredEvents = allEvents.filter(event => {
          console.log('🔍 Checking event for user:', {
            eventName: event.eventName,
            participants: event.participants,
            participant: event.participant,
            userEmail,
            needsEventFiltering,
            canViewAllEvents
          });
          
          // Check if user's email is in participants
          if (event.participants && Array.isArray(event.participants)) {
            const match = event.participants.some(p => 
              p.toLowerCase() === userEmail.toLowerCase()
            );
            console.log('📋 Array participants match:', match, event.participants);
            return match;
          } else if (event.participant) {
            const participants = event.participant.split(',').map(p => p.trim());
            const match = participants.some(p => 
              p.toLowerCase() === userEmail.toLowerCase()
            );
            console.log('📋 String participants match:', match, participants);
            console.log('📧 Comparing:', participants, 'with', userEmail);
            return match;
          }
          // If no participants defined, don't show to restricted users
          console.log('❌ No participants found for event');
          return false;
        });
        console.log(`👤 Permission-based filtering: ${allEvents.length} events -> ${filteredEvents.length} events visible to ${userEmail}`);
        console.log('📧 User email:', userEmail);
        console.log('📋 All events being checked:', allEvents.map(e => ({
          name: e.eventName,
          participants: e.participants,
          participant: e.participant
        })));
      }
      
      // Filter out duplicate events from shared calendars
      const uniqueEvents = [];
      const seenEvents = new Map();
      
      for (const event of filteredEvents) {
        // Create a unique key based on event details
        const eventKey = `${event.eventName?.toLowerCase()}-${event.day}-${event.month}-${event.year}-${event.hour || 'notime'}-${event.minute || 0}`;
        
        if (!seenEvents.has(eventKey)) {
          seenEvents.set(eventKey, event);
          uniqueEvents.push(event);
        } else {
          // If duplicate found, prefer personal events over class events
          const existingEvent = seenEvents.get(eventKey);
          if (event.isPersonalEvent && existingEvent.isClassEvent) {
            // Replace with personal event
            const index = uniqueEvents.findIndex(e => e.id === existingEvent.id);
            if (index !== -1) {
              uniqueEvents[index] = event;
              seenEvents.set(eventKey, event);
            }
          } else if (event.userId === currentUser.uid && existingEvent.userId !== currentUser.uid) {
            // Replace with current user's event
            const index = uniqueEvents.findIndex(e => e.id === existingEvent.id);
            if (index !== -1) {
              uniqueEvents[index] = event;
              seenEvents.set(eventKey, event);
            }
          }
          console.log(`🔍 Duplicate event filtered: "${event.eventName}" (${event.isClassEvent ? 'class event' : event.userId === currentUser.uid ? 'personal' : 'shared'})`);
        }
      }
      
      console.log('📊 Processing all events:', filteredEvents.length);
      console.log('📊 After duplicate filtering:', uniqueEvents.length);
      console.log('📜 First few events:', uniqueEvents.slice(0, 3).map(e => ({
        id: e.id,
        name: e.eventName,
        date: `${e.year}-${e.month}-${e.day}`,
        isClassEvent: e.isClassEvent || false
      })));
      setAcceptedEvents(uniqueEvents);
    };

    // Function to process and deduplicate pending events
    const processPendingEvents = async (allPendingEvents) => {
      // Filter events based on permissions - only show events they're assigned to if they don't have view all permission
      let filteredEvents = allPendingEvents;
      // IMPORTANT: Only filter if family role is loaded AND user needs event filtering (doesn't have view all permission)
      if (!familyRoleLoading && needsEventFiltering) {
        // Use user's email for matching
        const userEmail = currentUser.email;
        
        filteredEvents = allPendingEvents.filter(event => {
          // Check if user's email is in participants
          if (event.participants && Array.isArray(event.participants)) {
            return event.participants.some(p => 
              p.toLowerCase() === userEmail.toLowerCase()
            );
          } else if (event.participant) {
            const participants = event.participant.split(',').map(p => p.trim());
            return participants.some(p => 
              p.toLowerCase() === userEmail.toLowerCase()
            );
          }
          // If no participants defined, don't show to restricted users
          return false;
        });
        console.log(`👤 Permission-based filtering (pending): ${allPendingEvents.length} events -> ${filteredEvents.length} events visible to ${userEmail}`);
      }
      
      // Filter out duplicate events
      const uniqueEvents = [];
      const seenEvents = new Map();
      
      for (const event of filteredEvents) {
        // Create a unique key based on event details
        const eventKey = `${event.eventName?.toLowerCase()}-${event.day}-${event.month}-${event.year}-${event.hour || 'notime'}-${event.minute || 0}`;
        
        if (!seenEvents.has(eventKey)) {
          seenEvents.set(eventKey, event);
          uniqueEvents.push(event);
        } else {
          // If duplicate found, prefer personal events over class events
          const existingEvent = seenEvents.get(eventKey);
          if (event.isPersonalEvent && existingEvent.isClassEvent) {
            // Replace with class event
            const index = uniqueEvents.findIndex(e => e.id === existingEvent.id);
            if (index !== -1) {
              uniqueEvents[index] = event;
              seenEvents.set(eventKey, event);
            }
          }
        }
      }
      
      console.log('📊 Processing all pending events:', allPendingEvents.length);
      console.log('📊 After duplicate filtering:', uniqueEvents.length);
      setPendingEvents(uniqueEvents);
    };

    // Set up Arbor1 listeners immediately
    setupArbor1Listeners();

    // Cleanup listeners when component unmounts
    return () => {
      unsubscribePending();
      unsubscribeAccepted();
      if (unsubscribeArbor1) {
        unsubscribeArbor1();
      }
      if (unsubscribeArbor1Pending) {
        unsubscribeArbor1Pending();
      }
    };
  }, [currentUser, sharedUserIds, needsEventFiltering, familyRoleLoading]);

  // Track mobile screen size
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);
  
  // Open edit modal when eventToEdit changes and is not null
  useEffect(() => {
    if (eventToEdit) {
      setIsEditModalOpen(true);
    }
  }, [eventToEdit]);

  // Load PDF.js library
  useEffect(() => {
    // Load PDF.js script
    const pdfJsScript = document.createElement('script');
    pdfJsScript.src = 'https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.4.120/pdf.min.js';
    pdfJsScript.async = true;
    document.body.appendChild(pdfJsScript);

    // Set worker source once the script is loaded
    pdfJsScript.onload = () => {
      window.pdfjsLib.GlobalWorkerOptions.workerSrc = 'https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.4.120/pdf.worker.min.js';
    };

    return () => {
      document.body.removeChild(pdfJsScript);
    };
  }, []);

  // Get current date information for context
  const getCurrentDateInfo = () => {
    const now = new Date();
    return {
      day: now.getDate(),
      month: now.getMonth() + 1, // JavaScript months are 0-indexed
      year: now.getFullYear(),
      dayOfWeek: now.getDay(),
      dayNames: ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"],
      monthNames: ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"]
    };
  };

  // Function to extract dates from text
  const extractDates = async (textToAnalyze = null, sourceType = 'paste', preExtractedEvents = null) => {
    // If pre-extracted events are provided (e.g., from email), use them directly
    if (preExtractedEvents && Array.isArray(preExtractedEvents) && preExtractedEvents.length > 0) {
      setIsLoading(true);
      setError('');
      
      try {
        // Process pre-extracted events similar to how we process AI-extracted events
        const eventsToAdd = preExtractedEvents.map(event => ({
          ...event,
          category: event.category || 'life',
          createdAt: new Date().toISOString(),
          created: new Date().toISOString(),
          source: sourceType === 'email' ? 'email' : (event.source || sourceType),
          isDuplicate: false
        }));

        // Check for duplicates and filter them out
        const nonDuplicateEvents = eventsToAdd.filter(newEvent => {
          const duplicateInAccepted = checkForDuplicate(newEvent, acceptedEvents);
          if (duplicateInAccepted) {
            console.log(`Filtering out duplicate event "${newEvent.eventName}" found in accepted events`);
            return false;
          }
          
          const duplicateInPending = checkForDuplicate(newEvent, pendingEvents);
          if (duplicateInPending) {
            console.log(`Filtering out duplicate event "${newEvent.eventName}" found in pending events`);
            return false;
          }
          
          return true;
        });
        
        console.log(`Filtered out ${eventsToAdd.length - nonDuplicateEvents.length} duplicate events. Adding ${nonDuplicateEvents.length} unique events to Firebase.`);

        // Add non-duplicate events to Firebase pending events with userId
        const addEventPromises = nonDuplicateEvents.map(event =>
          addDoc(pendingEventsRef, { ...event, userId: currentUser.uid })
        );

        await Promise.all(addEventPromises);
        console.log('Successfully added pre-extracted events to pending events');
      } catch (error) {
        console.error('Error adding pre-extracted events:', error);
        setError('Error processing extracted events');
      } finally {
        setIsLoading(false);
      }
      
      return;
    }
    
    // If no text is provided, use the input text from the text area
    const textToProcess = textToAnalyze || inputText;

    if (!textToProcess.trim()) {
      setError('Please enter some text or upload a document');
      return;
    }
    
    // Validate sourceType
    const validSourceTypes = ['paste', 'document', 'manual', 'email', 'gcal'];
    if (!validSourceTypes.includes(sourceType)) {
      sourceType = 'paste'; // Default if invalid
    }

    setIsLoading(true);
    setError('');

    // Get current date info for context
    const dateInfo = getCurrentDateInfo();

    try {
      const response = await fetch('https://api.openai.com/v1/chat/completions', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${API_KEY}`
        },
        body: JSON.stringify({
          model: "gpt-4o",
          messages: [
            {
              role: "system",
              content: `
              Extract all dates, times, events, locations, participants, and categories from the provided text.

              Today is ${dateInfo.monthNames[dateInfo.month-1]} ${dateInfo.day}, ${dateInfo.year} (${dateInfo.dayNames[dateInfo.dayOfWeek]}).

              For dates:
              - Handle relative terms like "today", "tomorrow", "next Monday", etc.
              - When "next week" is mentioned without a specific day:
                * Interpret it as referring to next Monday by default
                * Example: "The meeting is next week" should be interpreted as next Monday
                * If any day is specified with "next week" (e.g., "next week on Wednesday"), use that specific day instead
              - If a year is not mentioned, assume ${dateInfo.year}
              - If a month is not mentioned, assume the current month (${dateInfo.month})
              - If only a day of week is mentioned (e.g., "on Tuesday"), determine the next occurrence of that day
              - If a date appears to be in the past for this year, consider if it might refer to next year

              For times:
              - Extract time information when available (e.g., "3:00 PM", "15:30", "morning", "noon", "afternoon")
              - Handle time ranges (e.g., "from 9AM to 5PM")

              For locations:
              - Extract specific location information when mentioned (e.g., addresses, venue names, online/virtual)
              - If no location is explicitly mentioned, set location to null

              For context:
              - Create a short description that captures the context or purpose of the event based on surrounding text
              - Limit descriptions to 1-2 sentences to keep them concise
              - Include any requirements or details if mentioned
              - Do NOT include location, participant names, or category information in the description, as these will be extracted separately

              For participants:
              - Extract names of people the event is for (e.g., "John's birthday", "meeting with Sarah", "appointment for the kids")
              - If no specific person is mentioned, set the participant to null

              For event categories:
              - Identify the type of event based on contextual clues
              - Categorize as one of: "work", "school", "sport", or "life"
              - "work": work-related meetings, deadlines, professional events
              - "school": classes, academic deadlines, school-related activities
              - "sport": athletic activities, games, matches, workouts
              - "life": social activities like dinners, parties, friend meetings, personal appointments
              - Every event MUST be classified as one of these categories
              - If unable to determine a clear category, set to "life"

              Return a JSON object with a property called 'events' containing an array where each item has:
              - day (numeric)
              - month (numeric)
              - year (numeric)
              - hasTime (boolean) - whether a specific time was mentioned
              - hour (numeric, 0-23) - if a time was mentioned
              - minute (numeric, 0-59) - if a time was mentioned
              - isAllDay (boolean) - true if no specific time was mentioned
              - timeText (string) - the original time text, or general time of day mentioned
              - eventName (string)
              - description (string) - a concise description derived from the context
              - location (string or null) - specific location information if mentioned, or null if no location was specified
              - participant (string or null) - name of who the event is for/with, or null if not specified
              - category (string) - one of: "work", "school", "sport", "life", "other"
              - originalDateText (string) - the original date text as it appeared in the input
              `
            },
            {
              role: "user",
              content: textToProcess
            }
          ],
          response_format: { type: "json_object" }
        })
      });

      if (!response.ok) {
        throw new Error('Failed to extract dates');
      }

      const data = await response.json();

      // Parse the response and extract events
      try {
        const content = JSON.parse(data.choices[0].message.content);
        if (Array.isArray(content.events) && content.events.length > 0) {
          // Prepare events with timestamp for Firebase and ensure all have a category
          const newEvents = content.events.map(event => ({
            ...event,
            category: event.category || 'life', // Ensure every event has a category, default to 'life'
            createdAt: new Date().toISOString(), // Add timestamp for sorting
            created: new Date().toISOString(), // For edit detection
            source: sourceType, // Track source of event for display in Recent Activity
            isDuplicate: false // Default to not duplicate
          }));

          // Check for duplicates by comparing with existing events and filter them out
          const nonDuplicateEvents = newEvents.filter(newEvent => {
            // Check for duplicates in accepted events first
            const duplicateInAccepted = checkForDuplicate(newEvent, acceptedEvents);
            if (duplicateInAccepted) {
              console.log(`Filtering out duplicate event "${newEvent.eventName}" found in accepted events`);
              return false; // Filter out the duplicate
            }
            
            // Then check for duplicates in pending events
            const duplicateInPending = checkForDuplicate(newEvent, pendingEvents);
            if (duplicateInPending) {
              console.log(`Filtering out duplicate event "${newEvent.eventName}" found in pending events`);
              return false; // Filter out the duplicate
            }
            
            return true; // Keep non-duplicate events
          });
          
          console.log(`Filtered out ${newEvents.length - nonDuplicateEvents.length} duplicate events. Adding ${nonDuplicateEvents.length} unique events to Firebase.`);

          // Only add non-duplicate events to Firebase with userId
          const addEventPromises = nonDuplicateEvents.map(event =>
            addDoc(pendingEventsRef, { ...event, userId: currentUser.uid })
          );

          // Wait for all events to be added
          await Promise.all(addEventPromises);

          // Clear the input field after successful extraction
          setInputText('');
        } else {
          setError('No events found in the text');
        }
      } catch (parseError) {
        console.error('Error parsing response:', parseError);
        setError('Error processing the extracted dates');
      }
    } catch (err) {
      console.error('Error calling API:', err);
      setError(`Error: ${err.message}`);
    } finally {
      setIsLoading(false);
    }
  };

  // Function to accept an event (move from pending to accepted)
  const acceptEvent = async (eventId) => {
    console.log('🟢 acceptEvent called with eventId:', eventId);
    console.log('🟢 Current pendingEvents:', pendingEvents);
    console.log('🟢 Pending event IDs:', pendingEvents.map(e => e.id));
    
    // Find the event to accept from pending events
    const eventToAccept = pendingEvents.find(event => event.id === eventId);
    console.log('🟢 Found event to accept:', eventToAccept);
    
    if (!eventToAccept) {
      console.log('🔴 Event not found in pendingEvents! Checking if ID types match...');
      const eventById = pendingEvents.find(event => String(event.id) === String(eventId));
      console.log('🔴 Event found by string comparison:', eventById);
    }

    if (eventToAccept) {
      // Prevent accepting class events from Arbor1 collections
      if (eventToAccept.isClassEvent || eventToAccept.collectionSource === 'Arbor1' || eventToAccept.collectionSource === 'Arbor1PendingEvents') {
        alert("Class events cannot be accepted individually. These are shared events for all students in your class.");
        return;
      }
      
      // Check if this event already exists in accepted events by ID
      const eventAlreadyExistsById = acceptedEvents.some(event => event.id === eventId);
      
      // Also check if this event is a duplicate of any existing accepted event
      const isDuplicate = checkForDuplicate(eventToAccept, acceptedEvents);

      if (!eventAlreadyExistsById && !isDuplicate) {
        try {
          // Remove undefined fields from the event object
          const cleanedEvent = Object.fromEntries(
            Object.entries(eventToAccept).filter(([_, value]) => value !== undefined)
          );
          
          // Add to accepted events in Firebase with category validation and userId
          const docRef = await addDoc(acceptedEventsRef, {
            ...cleanedEvent,
            userId: currentUser.uid,
            createdByName: currentUser.displayName || currentUser.email?.split('@')[0] || 'Unknown', // Add creator's name
            category: cleanedEvent.category || 'life', // Ensure event has a category
            createdAt: new Date().toISOString()
          });

          // Delete from pending events in Firebase using the correct ID
          const deleteId = eventToAccept.firestoreId || eventToAccept.id;
          await deleteDoc(doc(db, "pendingEvents", deleteId));
          
          // Let Firebase listeners handle state updates automatically
          // No immediate local state updates needed
          console.log(`Event "${eventToAccept.eventName}" moved to accepted events`);
        } catch (error) {
          console.error("Error accepting event:", error);
        }
      } else {
        console.log(`Prevented duplicate event "${eventToAccept.eventName}" from being added to accepted events`);
        // Still delete from pending events since we don't need it there
        try {
          const deleteId = eventToAccept.firestoreId || eventToAccept.id;
          await deleteDoc(doc(db, "pendingEvents", deleteId));
          
          // Firebase listener will handle removing from pending automatically
        } catch (error) {
          console.error("Error deleting duplicate pending event:", error);
        }
      }
    }
  };

  // Function to accept all pending events
  const acceptAllEvents = async () => {
    if (pendingEvents.length === 0) return;

    // Filter out any pending events that are already in accepted events by ID
    // or are duplicates of existing accepted events
    const newEventsToAccept = pendingEvents.filter(pendingEvent => {
      // Skip class events
      if (pendingEvent.isClassEvent || pendingEvent.collectionSource === 'Arbor1' || pendingEvent.collectionSource === 'Arbor1PendingEvents') {
        console.log(`Skipping class event "${pendingEvent.eventName}" when accepting all`);
        return false;
      }
      
      // First check by ID
      const alreadyExistsById = acceptedEvents.some(
        acceptedEvent => acceptedEvent.id === pendingEvent.id
      );
      
      if (alreadyExistsById) {
        return false; // Filter out if already exists by ID
      }
      
      // Then check for duplicates
      const isDuplicate = checkForDuplicate(pendingEvent, acceptedEvents);
      if (isDuplicate) {
        console.log(`Filtered out duplicate event "${pendingEvent.eventName}" when accepting all events`);
        return false; // Filter out duplicates
      }
      
      return true; // Keep non-duplicate events
    });

    console.log(`Accepting ${newEventsToAccept.length} out of ${pendingEvents.length} pending events (filtered out ${pendingEvents.length - newEventsToAccept.length} duplicates)`);
    
    if (newEventsToAccept.length > 0) {
      try {
        // Add all non-duplicate events to accepted collection with category validation and userId
        const addPromises = newEventsToAccept.map(event =>
          addDoc(acceptedEventsRef, {
            ...event,
            userId: currentUser.uid,
            category: event.category || 'life', // Ensure event has a category
            createdAt: new Date().toISOString()
          })
        );

        // Delete all events from pending collection (including duplicates)
        const deletePromises = pendingEvents.map(event => {
          const deleteId = event.firestoreId || event.id;
          return deleteDoc(doc(db, "pendingEvents", deleteId));
        });

        // Execute all operations
        await Promise.all([...addPromises, ...deletePromises]);
      } catch (error) {
        console.error("Error accepting all events:", error);
      }
    } else {
      // If all pending events are duplicates, just delete them all
      try {
        const deletePromises = pendingEvents.map(event => {
          const deleteId = event.firestoreId || event.id;
          return deleteDoc(doc(db, "pendingEvents", deleteId));
        });
        await Promise.all(deletePromises);
        console.log("All pending events were duplicates and have been removed");
      } catch (error) {
        console.error("Error deleting duplicate pending events:", error);
      }
    }
  };

  // Function to ignore/delete a pending event
  const ignoreEvent = async (eventId) => {
    console.log('🔴 ignoreEvent called with eventId:', eventId);
    console.log('🔴 Current pendingEvents:', pendingEvents);
    
    // Find the event in pendingEvents to see its actual structure
    const eventToDelete = pendingEvents.find(event => event.id === eventId);
    console.log('🔴 Event found in pendingEvents:', eventToDelete);
    
    if (!eventToDelete) {
      console.error('🔴 Event not found in pendingEvents array!');
      return;
    }
    
    // Prevent ignoring class events from Arbor1 collections
    if (eventToDelete.isClassEvent || eventToDelete.collectionSource === 'Arbor1' || eventToDelete.collectionSource === 'Arbor1PendingEvents') {
      alert("Class events cannot be ignored. These are shared events for all students in your class.");
      return;
    }
    
    // Use the firestoreId for deletion, not the temporary ID
    const deleteId = eventToDelete.firestoreId || eventToDelete.id;
    console.log('🔴 Using deleteId for Firestore:', deleteId);
    console.log('🔴 eventToDelete structure:', {
      id: eventToDelete.id,
      firestoreId: eventToDelete.firestoreId,
      originalId: eventToDelete.originalId || 'no originalId'
    });
    
    try {
      await deleteDoc(doc(db, "pendingEvents", deleteId));
      console.log('🔴 Successfully deleted from Firestore');
    } catch (error) {
      console.error('🔴 Error deleting from Firestore:', error);
      // If deletion fails, try with just the id property
      if (deleteId !== eventToDelete.id) {
        console.log('🔴 Retrying with fallback id:', eventToDelete.id);
        try {
          await deleteDoc(doc(db, "pendingEvents", eventToDelete.id));
          console.log('🔴 Fallback deletion successful');
        } catch (fallbackError) {
          console.error('🔴 Fallback deletion also failed:', fallbackError);
        }
      }
    }
  };

  // Function to delete all pending events
  const ignoreAllEvents = async () => {
    if (pendingEvents.length === 0) return;

    try {
      // Filter out class events before deleting
      const eventsToDelete = pendingEvents.filter(event => 
        !event.isClassEvent && event.collectionSource !== 'Arbor1' && event.collectionSource !== 'Arbor1PendingEvents'
      );
      
      if (eventsToDelete.length === 0) {
        alert("No personal events to ignore. Class events cannot be ignored.");
        return;
      }
      
      const deletePromises = eventsToDelete.map(event => {
        const deleteId = event.firestoreId || event.id;
        return deleteDoc(doc(db, "pendingEvents", deleteId));
      });

      await Promise.all(deletePromises);
    } catch (error) {
      console.error("Error deleting all pending events:", error);
    }
  };

  // Function to delete an accepted event
  const deleteAcceptedEvent = async (eventId) => {
    try {
      // Find the event to delete
      const eventToDelete = acceptedEvents.find(event => event.id === eventId);
      if (!eventToDelete) {
        console.error("Event not found for deletion");
        return;
      }

      // Handle deletion of class events
      if (eventToDelete.isClassEvent) {
        // Check if user is the admin of this class
        if (!isClassAdmin || eventToDelete.classID !== classInfo?.classID) {
          alert("Only class admins can delete class events.");
          return;
        }
        
        // Admin is deleting a class event - need to delete from all members
        const confirmDelete = window.confirm(
          `Are you sure you want to delete the class event "${eventToDelete.eventName}"? This will remove it from all class members' calendars.`
        );
        
        if (!confirmDelete) {
          return;
        }
        
        // Delete class event from all members
        try {
          const deleteResult = await classEventService.deleteClassEventFromAllMembers(
            eventToDelete.classEventId,
            eventToDelete.classID,
            currentUser.uid
          );
          console.log(`✅ Class event deleted from ${deleteResult.deletedFromMembers} members`);
          
          // Continue with deleting the admin's copy below
        } catch (deleteError) {
          console.error('Error deleting class event from members:', deleteError);
          alert('Failed to delete class event from all members. Please try again.');
          return;
        }
      }
      
      // Prevent deletion of Arbor1 events
      if (eventToDelete.collectionSource === 'Arbor1' || eventToDelete.collectionSource === 'Arbor1PendingEvents') {
        alert("School events from Arbor1 cannot be deleted.");
        return;
      }

      // Use the firestoreId for deletion
      const deleteId = eventToDelete.firestoreId || eventToDelete.id;
      await deleteDoc(doc(db, "acceptedEvents", deleteId));
      
      // Also update local state to remove the event
      setAcceptedEvents(prevEvents => prevEvents.filter(event => event.id !== eventId));
    } catch (error) {
      console.error("Error deleting event:", error);
    }
  };

  // Function to delete all events with duplicate checking
  const deleteAllAcceptedEvents = async () => {
    if (acceptedEvents.length === 0) {
      console.log('No accepted events to delete');
      return;
    }
    
    // Confirm with user
    const confirmDelete = window.confirm(`Are you sure you want to delete all ${acceptedEvents.length} accepted events?`);
    if (!confirmDelete) return;

    try {
      const deletePromises = acceptedEvents.map(event => 
        deleteDoc(doc(db, "acceptedEvents", event.firestoreId || event.id))
      );
      
      await Promise.all(deletePromises);
      
      // Clear local state
      setAcceptedEvents([]);
      
      console.log('All accepted events deleted');
    } catch (error) {
      console.error("Error deleting all accepted events:", error);
    }
  };

  // Function to add new events from recent activity to accepted events
  const addRecentToAccepted = async (events) => {
    if (!events || events.length === 0) return;
    
    try {
      const addPromises = events.map(event => {
        // Ensure the event has all required fields
        const completeEvent = {
          ...event,
          category: event.category || 'life',
          createdAt: new Date().toISOString(),
          created: new Date().toISOString()
        };
        return addDoc(acceptedEventsRef, completeEvent);
      });
      
      await Promise.all(addPromises);
      
      // Clear only the accepted events from localStorage
      const storedData = localStorage.getItem('eventData');
      if (storedData) {
        const parsedData = JSON.parse(storedData);
        // Keep pending events, remove accepted events
        const updatedData = {
          pendingEvents: parsedData.pendingEvents || [],
          acceptedEvents: []
        };
        localStorage.setItem('eventData', JSON.stringify(updatedData));
      }
      
      console.log('Added events from recent activity to accepted events');
      setAcceptedEvents(events);
    } catch (error) {
      console.error("Error adding recent events to accepted:", error);
    }
  };

  // Function to update an accepted event
  const updateAcceptedEvent = async (updatedEvent) => {
    try {
      console.log("Updating event:", updatedEvent);

      // Find the original event to check its current properties
      const originalEvent = acceptedEvents.find(event => event.id === updatedEvent.id);

      // Prevent editing of class events from Arbor1 collection
      if (originalEvent?.collectionSource === 'Arbor1') {
        alert("Class events cannot be edited. These are shared events for all students in your class.");
        return;
      }
      
      // Handle class event editing permissions
      if (originalEvent?.isClassEvent) {
        // Check if user can edit this class event
        try {
          const editPermission = await classEventService.canUserEditClassEvent(
            currentUser.uid, 
            originalEvent.classEventId, 
            originalEvent.classID
          );
          
          if (!editPermission.canEdit) {
            alert(editPermission.reason);
            return;
          }
        } catch (error) {
          console.error('Error checking class event edit permissions:', error);
          alert("Unable to verify edit permissions for this class event.");
          return;
        }
      }

      // Special case for directly added events (e.g., from Google Calendar sync)
      if (updatedEvent.isDirectAdd) {
        console.log("This is a direct add with existing Firestore ID:", updatedEvent.firestoreId);
        
        // Enhanced duplicate checking logic for direct adds (especially Google Calendar events)
        // Simply update the local state, but with thorough duplicate detection
        setAcceptedEvents(prev => {
          // More thorough duplicate checking
          const isDuplicate = prev.some(e => 
            // Check by Firebase ID
            (e.id === updatedEvent.id || e.firestoreId === updatedEvent.firestoreId) ||
            // Check by Google Calendar ID if available
            (e.googleId && updatedEvent.googleId && e.googleId === updatedEvent.googleId) ||
            // Check by key event properties
            (e.eventName === updatedEvent.eventName && 
             e.day === updatedEvent.day && 
             e.month === updatedEvent.month && 
             e.year === updatedEvent.year &&
             // For events at the same time, compare hour/minute if both have times
             ((e.hasTime && updatedEvent.hasTime) ? 
                (e.hour === updatedEvent.hour && e.minute === updatedEvent.minute) : 
                true))
          );
          
          if (isDuplicate) {
            console.log(`Duplicate detected for event "${updatedEvent.eventName}" - not adding to state`);
            return prev;
          }
          
          console.log(`Adding new event to state: "${updatedEvent.eventName}" on ${updatedEvent.month}/${updatedEvent.day}/${updatedEvent.year}`);
          return [updatedEvent, ...prev];
        });
        
        return; // Skip the rest of the update logic
      }

      // First, ensure we have the firestoreId - it's crucial for the update
      const firestoreId = updatedEvent.firestoreId || updatedEvent.id;

      if (!firestoreId) {
        console.error("No valid ID found for update");
        alert("Could not update this event. Missing ID information.");
        return;
      }

      console.log("Using firestoreId for update:", firestoreId);

      // For pending events being edited, they need to be moved to accepted events
      let isFromPendingEvents = false;

      // Check if event is from pendingEvents
      if (pendingEvents.some(event => event.id === updatedEvent.id)) {
        console.log("Event is from pending events, will move to accepted events");
        isFromPendingEvents = true;
      }
      // If not in pending events, check if it exists in accepted events
      else if (!isFromPendingEvents && !acceptedEvents.some(event => event.id === updatedEvent.id)) {
        console.log("Event not found in accepted events, might be new");
        // We'll continue anyway since we'll create a new document if needed
      }

      // Create a sanitized copy for Firestore
      const { id, firestoreId: _, ...eventData } = updatedEvent;

      if (isFromPendingEvents) {
        // Add to accepted events with userId
        const docRef = await addDoc(acceptedEventsRef, {
          ...eventData,
          userId: currentUser.uid,
          createdByName: currentUser.displayName || currentUser.email?.split('@')[0] || 'Unknown', // Add creator's name
          createdAt: new Date().toISOString()
        });
        console.log("Added to accepted events with ID:", docRef.id);

        // Delete from pending events
        await deleteDoc(doc(db, "pendingEvents", updatedEvent.id));
        console.log("Deleted from pending events");

        // Update local state (remove from pending, add to accepted)
        setPendingEvents(prev => prev.filter(event => event.id !== updatedEvent.id));
        setAcceptedEvents(prev => [
          { ...updatedEvent, id: docRef.id, firestoreId: docRef.id },
          ...prev
        ]);
      } else {
        // Check if this is a category change to 'class' - need to detect this before updating
        const originalEvent = acceptedEvents.find(event => event.id === updatedEvent.id);
        const isBecomingClassEvent = originalEvent && 
                                   originalEvent.category !== 'class' && 
                                   updatedEvent.category === 'class';

        // Update existing event in acceptedEvents
        // Add lastModified timestamp to track edits
        const eventDataWithTimestamp = {
          ...eventData,
          lastModified: new Date().toISOString(),
          // Add class event properties if converting to class event
          ...(isBecomingClassEvent && {
            isClassEvent: true,
            classID: updatedEvent.classID || null,
            className: updatedEvent.className || null
          })
        };
        
        try {
          // First, try direct update with the firestoreId we have
          const eventRef = doc(db, "acceptedEvents", firestoreId);
          await updateDoc(eventRef, eventDataWithTimestamp);
          console.log("Event updated using direct firestoreId reference");

          // If this event is becoming a class event, distribute it to all class members
          if (isBecomingClassEvent && updatedEvent.classID) {
            console.log('📚 Event category changed to class - distributing to class members...');
            try {
              const result = await classEventService.convertToClassEvent(
                updatedEvent, 
                updatedEvent.classID, 
                firestoreId
              );
              console.log(`✅ Event distributed to ${result.memberCount} class members`);
              alert(`Event "${updatedEvent.eventName}" has been converted to a class event and shared with ${result.memberCount} class members!`);
            } catch (classError) {
              console.error('Error distributing class event:', classError);
              alert('Event was updated but failed to distribute to class members. Please try again.');
            }
          }
          // If this is an existing class event being edited
          else if (originalEvent?.isClassEvent && originalEvent?.classEventId) {
            // Check if the user is an admin
            if (isClassAdmin && originalEvent.classID === classInfo?.classID) {
              // Admin editing class event - sync to all members
              console.log('📚 Class event being edited by admin - syncing to all members...');
              console.log('🔍 Original event details:', originalEvent);
              console.log('🔑 Using classEventId:', originalEvent.classEventId);
              
              // Add flag to track that admin has updated this event
              eventDataWithTimestamp.lastAdminUpdate = new Date().toISOString();
              eventDataWithTimestamp.lastAdminUpdateBy = currentUser.uid;
              
              try {
                const syncResult = await classEventService.syncClassEventToAllMembers(
                  updatedEvent,
                  originalEvent.classEventId,
                  currentUser.uid
                );
                console.log(`✅ Class event synced to ${syncResult.syncedToMembers} members`);
                alert(`Class event "${updatedEvent.eventName}" has been updated for all ${syncResult.syncedToMembers} class members!`);
              } catch (syncError) {
                console.error('Error syncing class event changes:', syncError);
                alert('Event was updated but failed to sync changes to all class members. Please try again.');
              }
            } else {
              // Member editing their personal copy of class event
              console.log('📝 Class member editing personal copy of class event');
              console.log('🔍 Member ID:', currentUser.uid);
              console.log('📋 Event being personalized:', updatedEvent.eventName);
              
              // Add flag to track this is a personalized version
              eventDataWithTimestamp.isPersonalized = true;
              eventDataWithTimestamp.personalizedBy = currentUser.uid;
              eventDataWithTimestamp.personalizedAt = new Date().toISOString();
              // Keep track of the last admin update timestamp to know if we should override later
              eventDataWithTimestamp.lastKnownAdminUpdate = originalEvent.lastAdminUpdate || originalEvent.createdAt;
              
              // The update already happened above, just log success
              console.log('✅ Personal copy of class event updated successfully');
            }
          }

          // Update local state
          setAcceptedEvents(prevEvents =>
            prevEvents.map(event =>
              event.id === updatedEvent.id ? {
                ...updatedEvent,
                firestoreId, // Preserve the firestoreId
                lastModified: new Date().toISOString() // Update lastModified timestamp
              } : event
            )
          );
        } catch (updateError) {
          console.error("Direct update failed:", updateError);

          // Fallback: Query for the document by key properties and update
          console.log("Attempting to find document by query...");
          const q = query(acceptedEventsRef,
            where("eventName", "==", eventData.eventName),
            where("day", "==", eventData.day),
            where("month", "==", eventData.month),
            where("year", "==", eventData.year)
          );

          const querySnapshot = await getDocs(q);

          if (querySnapshot.empty) {
            console.error("No document found by query for update");
            
            // Last resort: Create a new document
            console.log("Creating new document as fallback");
            const newDocRef = await addDoc(acceptedEventsRef, {
              ...eventData,
              createdAt: new Date().toISOString(),
              lastModified: new Date().toISOString()
            });

            // Update local state with the new document
            setAcceptedEvents(prevEvents => [
              {
                ...updatedEvent,
                id: newDocRef.id,
                firestoreId: newDocRef.id
              },
              ...prevEvents.filter(event => event.id !== updatedEvent.id)
            ]);
          } else {
            // Found a document, use the first match
            const docToUpdate = querySnapshot.docs[0];
            await updateDoc(docToUpdate.ref, eventDataWithTimestamp);
            console.log("Event updated using query reference");

            // If this event is becoming a class event, distribute it to all class members
            if (isBecomingClassEvent && updatedEvent.classID) {
              console.log('📚 Event category changed to class (fallback path) - distributing to class members...');
              try {
                const result = await classEventService.convertToClassEvent(
                  updatedEvent, 
                  updatedEvent.classID, 
                  docToUpdate.id
                );
                console.log(`✅ Event distributed to ${result.memberCount} class members`);
                alert(`Event "${updatedEvent.eventName}" has been converted to a class event and shared with ${result.memberCount} class members!`);
              } catch (classError) {
                console.error('Error distributing class event (fallback):', classError);
                alert('Event was updated but failed to distribute to class members. Please try again.');
              }
            }

            // Update local state with the correct Firestore ID
            setAcceptedEvents(prevEvents =>
              prevEvents.map(event =>
                event.id === updatedEvent.id ? {
                  ...updatedEvent,
                  id: docToUpdate.id,
                  firestoreId: docToUpdate.id,
                  lastModified: new Date().toISOString()
                } : event
              )
            );
          }
        }
      }
    } catch (error) {
      console.error("Error updating event:", error);
      alert(`Error updating event: ${error.message}`);
    }
  };

  // Function to delete all events (debugging function)
  const deleteAllEvents = async () => {
    try {
      // Delete all pending events
      const pendingSnapshot = await getDocs(pendingEventsRef);
      const pendingDeletePromises = pendingSnapshot.docs.map(doc => deleteDoc(doc.ref));
      await Promise.all(pendingDeletePromises);

      // Delete all accepted events
      const acceptedSnapshot = await getDocs(acceptedEventsRef);
      const acceptedDeletePromises = acceptedSnapshot.docs.map(doc => deleteDoc(doc.ref));
      await Promise.all(acceptedDeletePromises);

      console.log("All events deleted successfully");
    } catch (error) {
      console.error("Error deleting all events:", error);
    }
  };

  // Function to format date and time in a readable way
  const formatDateTime = (event, isCalendarPage = false) => {
    // Format time string using "7:00 AM - 11:30 PM" format
    let timeStr = '';
    if (event.hasTime && event.hour !== null && event.minute !== null) {
      const hour12 = event.hour % 12 === 0 ? 12 : event.hour % 12;
      const ampm = event.hour < 12 ? 'AM' : 'PM'; // Uppercase AM/PM
      
      // Always include minutes with leading zero if needed
      const formattedMinutes = String(event.minute || 0).padStart(2, '0');
      
      // Format start time as "7:00 AM"
      timeStr = `${hour12}:${formattedMinutes} ${ampm}`;
      
      // Always show an end time
      let endHour24, endMinute, endHour12, endAmpm;
      
      // First check if the event has an explicit end time
      if (event.endHour !== undefined && event.endHour !== null) {
        endHour24 = event.endHour;
        endMinute = event.endMinute || 0;
      }
      // If we have a duration, calculate the end time
      else if (event.eventDuration !== undefined && event.eventDuration > 0) {
        // Calculate end time from start time + duration
        const startMinutes = (event.hour * 60) + (event.minute || 0);
        const endMinutes = startMinutes + event.eventDuration;
        
        // Convert to hours and minutes
        endHour24 = Math.floor(endMinutes / 60) % 24;
        endMinute = endMinutes % 60;
      }
      // If no end time or duration is provided, add a default 1-hour duration
      else {
        // Calculate end time from start time + 1 hour
        const startMinutes = (event.hour * 60) + (event.minute || 0);
        const endMinutes = startMinutes + 60; // Default 1 hour duration
        
        // Convert to hours and minutes
        endHour24 = Math.floor(endMinutes / 60) % 24;
        endMinute = 0; // Default to on the hour
      }
      
      // Format the end time components
      endHour12 = endHour24 % 12 === 0 ? 12 : endHour24 % 12;
      endAmpm = endHour24 < 12 ? 'AM' : 'PM'; // Uppercase AM/PM
      const formattedEndMinutes = String(endMinute).padStart(2, '0');
      
      // Add the end time to create format like "7:00 AM - 8:00 PM"
      timeStr += ` - ${endHour12}:${formattedEndMinutes} ${endAmpm}`;
    } else if (event.timeText) {
      // For custom time text, try to standardize if possible
      if (event.timeText.toLowerCase() === 'all day') {
        timeStr = 'All day';
      } else {
        timeStr = event.timeText;
      }
    }

    // For calendar page, return only the time
    if (isCalendarPage) {
      return timeStr ? timeStr : 'All day';
    }

    // Get the current date
    const today = new Date();
    const tomorrow = new Date(today);
    tomorrow.setDate(tomorrow.getDate() + 1);

    // Create a date object for the event
    const eventDate = new Date(event.year, event.month - 1, event.day);

    // Format the date part
    let dateStr = '';

    // Check if the event is today or tomorrow
    if (eventDate.toDateString() === today.toDateString()) {
      dateStr = 'Today';
    } else if (eventDate.toDateString() === tomorrow.toDateString()) {
      dateStr = 'Tomorrow';
    } else {
      // Add ordinal suffix (st, nd, rd, th) to the day number
      const getOrdinalSuffix = (day) => {
        if (day > 3 && day < 21) return 'th';
        switch (day % 10) {
          case 1: return 'st';
          case 2: return 'nd';
          case 3: return 'rd';
          default: return 'th';
        }
      };

      const months = [
        "January", "February", "March", "April", "May", "June",
        "July", "August", "September", "October", "November", "December"
      ];

      // Use shorter month names on mobile
      const shortMonths = [
        "Jan", "Feb", "Mar", "Apr", "May", "Jun",
        "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"
      ];

      const currentMonths = isMobile ? shortMonths : months;

      dateStr = `${currentMonths[event.month-1]} ${event.day}${getOrdinalSuffix(event.day)}`;

      // Only add year if it's not the current year
      if (event.year !== today.getFullYear()) {
        dateStr += `, ${event.year}`;
      }
    }

    // Return combination of date and time
    return `${dateStr}, ${timeStr ? timeStr : 'All day'}`;
  };

  // Get the style for a category
  const getCategoryStyles = (category) => {
    const styles = {
      backgroundColor: '#f0f0f0',
      color: '#666666'
    };

    switch(category) {
      case 'work':
        styles.backgroundColor = '#e9f3ff'; // Light blue
        styles.color = '#1c83c9'; // Blue
        break;
      case 'school':
      case 'class':
        styles.backgroundColor = '#eeeaff'; // Light purple
        styles.color = '#5550B7'; // Purple
        break;
      case 'sport':
        styles.backgroundColor = '#ffe6e6'; // Light red
        styles.color = '#E63946'; // Red
        break;
      case 'life':
        styles.backgroundColor = '#e6fffd'; // Light green
        styles.color = '#2A9D8F'; // Green
        break;
      default:
        break;
    }

    return styles;
  };

  // No need for localStorage event loading anymore

  // Main app content
  const appContent = (
    <Router>
      {/* Main app container - wraps entire application */}
      <AppContainerWrapper>
        {/* Desktop navigation bar - only shows on screens > 768px */}
        {!isMobile && (
          <div className="top-navigation">
            <Navigation />
          </div>
        )}
        {/* Main content container - holds all page routes */}
        <div className="container">
          <Routes>
            <Route path="/login" element={<Login />} />
            <Route path="/signup" element={<SignUp />} />
            <Route path="/" element={
              <PrivateRoute>
                <PageTransition>
                  <New
                  acceptedEvents={acceptedEvents}
                  pendingEvents={pendingEvents}
                  sharedUserIds={sharedUserIds}
                  acceptEvent={async (eventId) => {
                    // If this is a direct event object (from manual creation)
                    if (typeof eventId === 'object') {
                      // Check if this is a duplicate of any existing accepted event
                      const isDuplicate = checkForDuplicate(eventId, acceptedEvents);
                      
                      if (isDuplicate) {
                        console.log(`Prevented duplicate manual event "${eventId.eventName}" from being added in New route`);
                        alert(`This event appears to be a duplicate of an existing event and was not added.`);
                        return;
                      }
                      
                      try {
                        // Check if this is a class event
                        if (eventId.isClassEvent && eventId.classID) {
                          console.log('📚 Creating class event for classID:', eventId.classID);
                          
                          const result = await classEventService.createClassEvent(eventId, eventId.classID);
                          console.log(`✅ Class event created and distributed to ${result.memberCount} members`);
                          
                          alert(`Class event "${eventId.eventName}" has been created and shared with all class members!`);
                        } else {
                          // Regular personal event
                          await addDoc(acceptedEventsRef, {
                            ...eventId,
                            userId: currentUser.uid,
                            createdAt: new Date().toISOString()
                          });
                        }
                      } catch (error) {
                        console.error("Error adding manual event:", error);
                      }
                    } else {
                      // Otherwise, normal flow via ID
                      acceptEvent(eventId);
                    }
                  }}
                  ignoreEvent={ignoreEvent}
                  acceptAllEvents={acceptAllEvents}
                  ignoreAllEvents={ignoreAllEvents}
                  deleteAcceptedEvent={deleteAcceptedEvent}
                  updateAcceptedEvent={updateAcceptedEvent}
                  getCategoryStyles={getCategoryStyles}
                  formatDateTime={formatDateTime}
                  isMobile={isMobile}
                  setEventToEdit={(event) => {
                    setEventToEdit(event);
                    setIsEditModalOpen(true);
                  }}
                  />
                </PageTransition>
              </PrivateRoute>
            } />

            <Route path="/calendar" element={
              <PrivateRoute>
                <PageTransition>
                  <CalendarPage
                    key="calendar-page" 
                    acceptedEvents={acceptedEvents}
                    deleteAcceptedEvent={deleteAcceptedEvent}
                    updateAcceptedEvent={updateAcceptedEvent}
                    getCategoryStyles={getCategoryStyles}
                    formatDateTime={formatDateTime}
                    isMobile={isMobile}
                    setEventToEdit={(event) => {
                      setEventToEdit(event);
                      setIsEditModalOpen(true);
                    }}
                    acceptEvent={async (eventId) => {
                      // If this is a direct event object (from manual creation)
                      if (typeof eventId === 'object') {
                        // Check if this is a duplicate of any existing accepted event
                        const isDuplicate = checkForDuplicate(eventId, acceptedEvents);
                        
                        if (isDuplicate) {
                          console.log(`Prevented duplicate manual event "${eventId.eventName}" from being added`);
                          alert(`This event appears to be a duplicate of an existing event and was not added.`);
                          return;
                        }
                        
                        try {
                          // Check if this is a class event
                          if (eventId.isClassEvent && eventId.classID) {
                            console.log('📚 Creating class event for classID:', eventId.classID);
                            
                            const result = await classEventService.createClassEvent(eventId, eventId.classID);
                            console.log(`✅ Class event created and distributed to ${result.memberCount} members`);
                            
                            alert(`Class event "${eventId.eventName}" has been created and shared with all class members!`);
                          } else {
                            // Regular personal event
                            const docRef = await addDoc(acceptedEventsRef, {
                              ...eventId,
                              userId: currentUser.uid,
                              createdAt: new Date().toISOString()
                            });
                            console.log('✅ Manual event from CalendarPage added to Firebase with ID:', docRef.id);
                          }
                        } catch (error) {
                          console.error("Error adding manual event from CalendarPage:", error);
                        }
                      } else {
                        // Otherwise, normal flow via ID
                        acceptEvent(eventId);
                      }
                    }}
                    API_KEY={API_KEY}
                  />
                </PageTransition>
              </PrivateRoute>
            } />

            <Route path="/upload" element={
              <PrivateRoute>
                <PageTransition>
                  <Upload
                  inputText={inputText}
                  setInputText={setInputText}
                  isLoading={isLoading}
                  error={error}
                  extractDates={extractDates}
                  pendingEvents={pendingEvents}
                  acceptEvent={async (eventId) => {
                    console.log('🚀 acceptEvent called in Upload route with:', eventId);
                    // If this is a direct event object (from manual creation)
                    if (typeof eventId === 'object') {
                      console.log('📦 Manual event detected, checking for duplicates before adding to Firebase');
                      
                      // Check if this is a duplicate of any existing accepted event
                      const isDuplicate = checkForDuplicate(eventId, acceptedEvents);
                      
                      if (isDuplicate) {
                        console.log(`🚫 Prevented duplicate manual event "${eventId.eventName}" from being added`);
                        alert(`This event appears to be a duplicate of an existing event and was not added.`);
                        return;
                      }
                      
                      try {
                        // Check if this is a class event
                        if (eventId.isClassEvent && eventId.classID) {
                          console.log('📚 Creating class event for classID:', eventId.classID);
                          
                          const result = await classEventService.createClassEvent(eventId, eventId.classID);
                          console.log(`✅ Class event created and distributed to ${result.memberCount} members`);
                          
                          alert(`Class event "${eventId.eventName}" has been created and shared with all class members!`);
                        } else {
                          // Regular personal event
                          const docRef = await addDoc(acceptedEventsRef, {
                            ...eventId,
                            userId: currentUser.uid,
                            createdAt: new Date().toISOString()
                          });
                          console.log('✅ Manual event successfully added to Firebase with ID:', docRef.id);

                          // DIRECT APPROACH: Immediately update the local state
                          setAcceptedEvents(prev => [
                            {
                              ...eventId,
                              id: docRef.id,
                              firestoreId: docRef.id
                            },
                            ...prev
                          ]);
                        }

                        // Navigate to calendar after a delay
                        setTimeout(() => {
                          window.location.href = '/calendar';
                        }, 1000);

                      } catch (error) {
                        console.error("❌ Error adding manual event:", error);
                        alert(`Error adding event: ${error.message}`);
                      }
                    } else {
                      // Otherwise, normal flow via ID
                      console.log('🔄 Regular event ID, using acceptEvent flow');
                      acceptEvent(eventId);
                    }
                  }}
                  ignoreEvent={ignoreEvent}
                  acceptAllEvents={acceptAllEvents}
                  ignoreAllEvents={ignoreAllEvents}
                  getCategoryStyles={getCategoryStyles}
                  formatDateTime={formatDateTime}
                  API_KEY={API_KEY}
                  isMobile={isMobile}
                  eventToEdit={eventToEdit}
                  setEventToEdit={setEventToEdit}
                  updateAcceptedEvent={updateAcceptedEvent}
                  />
                </PageTransition>
              </PrivateRoute>
            } />
            
            <Route path="/share" element={
              <PrivateRoute>
                <PageTransition>
                  <SharePage refreshSharedUsers={refreshSharedUsers} />
                </PageTransition>
              </PrivateRoute>
            } />
            
            <Route path="/account-settings" element={
              <PrivateRoute>
                <PageTransition>
                  <AccountSettingsPage />
                </PageTransition>
              </PrivateRoute>
            } />
            
            <Route path="/family-share" element={
              <PrivateRoute>
                <PageTransition>
                  <FamilySharePage />
                </PageTransition>
              </PrivateRoute>
            } />
            
            <Route path="/class-setup" element={
              <PrivateRoute>
                <PageTransition>
                  <ClassSetupPage />
                </PageTransition>
              </PrivateRoute>
            } />
            
            <Route path="/class-invite/:invitationId" element={
              <PrivateRoute>
                <PageTransition>
                  <ClassInvitePage />
                </PageTransition>
              </PrivateRoute>
            } />
            
            <Route path="/share-debug" element={
              <PrivateRoute>
                <PageTransition>
                  <ShareDebug />
                </PageTransition>
              </PrivateRoute>
            } />
            
            <Route path="/migrate-users" element={
              <PrivateRoute>
                <PageTransition>
                  <UserMigration />
                </PageTransition>
              </PrivateRoute>
            } />
            
            <Route path="/update-arbor1" element={
              <PrivateRoute>
                <PageTransition>
                  <UpdateArbor1 />
                </PageTransition>
              </PrivateRoute>
            } />
            
          </Routes>
        </div>

        {/* Mobile bottom navigation bar - hidden on calendar page for mobile */}
        <MobileNavigation isMobile={isMobile} />

        {/* Global modal for editing events - appears over all content when isEditModalOpen is true */}
        <EditEventModal
          event={eventToEdit}
          onSave={updateAcceptedEvent}
          isOpen={isEditModalOpen}
          onClose={() => setIsEditModalOpen(false)}
        />
      </AppContainerWrapper>
    </Router>
  );

  // Wrap the app content with the mobile container for desktop users
  return <MobileViewportContainer>{appContent}</MobileViewportContainer>;
}

function App() {
  return (
    <AuthProvider>
      <AppContent />
    </AuthProvider>
  );
}

export default App;